import { createContext, useCallback, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useStudentCourses } from '../hooks/useLmsData';
import {
  invalidateAdminCache,
  invalidateStudentCache,
} from '../services/lmsDataCache';

const LmsDataContext = createContext(null);

export const LmsDataProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const isStudent = currentUser?.role === 'student';
  const userId = isStudent ? currentUser?.uid : null;
  const studentWorkspace = useStudentCourses(userId, { enabled: isStudent });

  const invalidateStudent = useCallback(() => {
    if (userId) {
      invalidateStudentCache(userId);
    }
    studentWorkspace.refresh();
  }, [studentWorkspace, userId]);

  const invalidateAdmin = useCallback(() => {
    invalidateAdminCache();
  }, []);

  const value = useMemo(
    () => ({
      studentWorkspace,
      invalidateStudent,
      invalidateAdmin,
      studentUserId: userId,
    }),
    [invalidateAdmin, invalidateStudent, studentWorkspace, userId],
  );

  return (
    <LmsDataContext.Provider value={value}>{children}</LmsDataContext.Provider>
  );
};

export const useLmsDataContext = () => useContext(LmsDataContext);

export const useStudentWorkspace = () => {
  const context = useLmsDataContext();
  const { currentUser } = useAuth();
  const fallback = useStudentCourses(
    currentUser?.role === 'student' ? currentUser?.uid : null,
    { enabled: !context && currentUser?.role === 'student' },
  );

  if (context?.studentUserId === currentUser?.uid) {
    return context.studentWorkspace;
  }

  return fallback;
};
